// src/components/programs/ProgramForm/helpers/formInitializer.js

import { INITIAL_FORM_DATA } from "./formConstants";
import { generateDayBoxes } from "../../../../utils/programs/dateCalculations";
import { extractPricingUnitCode } from "../Step1Sections/utils/pricingUnitHelper";


/**
 * Helper function to merge product details with product data
 * The API returns pricing data in a separate 'details' array
 */
const mergeProductWithDetails = (product, allDetails) => {
    // Find matching detail by product_id
    const detail = allDetails.find(d => d.product_id === product.product_id);

    if (!detail) {
        return null; // Skip products without details
    }

    return {
        product_id: product.product_id,
        product_data: product, // Full product object from days
        detail_data: detail,   // Pricing data from details
    };
};

/**
 * Helper function to build dynamic fields object from product data
 */
const buildDynamicFields = (product) => {
    const fields = {};

    if (product.time !== null && product.time !== undefined) {
        fields.time = product.time;
    }
    if (product.pickup_time !== null && product.pickup_time !== undefined) {
        fields.pickup_time = product.pickup_time;
    }
    if (product.city_id !== null && product.city_id !== undefined) {
        fields.city_id = product.city_id;
    }
    if (product.units !== null && product.units !== undefined) {
        fields.units = product.units;
    }
    if (product.nights !== null && product.nights !== undefined) {
        fields.nights = product.nights;
    }

    return fields;
};

/**
 * Initialize form data from existing program data (for edit mode)
 */
export const initializeFormData = (programData) => {
    if (!programData) return INITIAL_FORM_DATA;

    // ✅ طباعة للتشخيص
    // console.log('📦 Form Initializer - Program Data:', programData);
    // console.log('📦 Form Initializer - Details:', programData.details);

    // ============================================
    // 1. Map Pax Types
    // ============================================
    const paxTypes = programData.pax_types?.map((pt) => ({
        pax_type_id: pt.pax_type_id || pt.id,
        pax_type_name: pt.name || { ar: '', en: '' },
        count: parseInt(pt.quantity) || 0,
    })) || [];

    // ============================================
    // 2. Map Itinerary Days with Products
    // ============================================
    const allDetails = programData.details || [];

    const itinerary = programData.days?.map((day) => {
        // Map products for this day
        const products = day.products?.map((product) => {
            // Merge product with its pricing details
            const merged = mergeProductWithDetails(product, allDetails);

            if (!merged) {
                // console.warn('⚠️ No details found for product:', product.product_id);
                return null; // Skip if no details found
            }

            const { product_data, detail_data } = merged;

            // ✅ طباعة للتشخيص
            // console.log('🔍 Product Data:', product_data);
            // console.log('🔍 Detail Data:', detail_data);

            // Extract pricing unit code from product_data or detail_data.product
            const pricingUnitCode = extractPricingUnitCode(product_data) ||
                extractPricingUnitCode(detail_data.product);

            // ✅ جلب السعر الأصلي للبيع من detail_data.original_sell_price
            const originalSellPrice = parseFloat(detail_data.original_sell_price) ||
                parseFloat(detail_data.product?.price) ||
                0;

            // ✅ جلب سعر التكلفة الأصلي من detail_data.cost_price
            const originalCostPrice = parseFloat(detail_data.cost_price) ||
                parseFloat(detail_data.product?.cost_price) ||
                0;

            // ✅ جلب السعر المخصص (إذا تم التعديل)
            const customPrice = detail_data.custom_price
                ? parseFloat(detail_data.custom_price)
                : null;

            // ✅ تحديد السعر الحالي والحالة
            const currentPrice = customPrice !== null ? customPrice : originalSellPrice;
            const isPriceEdited = customPrice !== null && customPrice !== originalSellPrice;

            // Build product object for form
            return {
                instance_id: product_data.id || `temp_${Date.now()}_${Math.random()}`,

                // Product snapshot (basic info)
                product_snapshot: {
                    product_id: product_data.product_id || product_data.id,
                    title: product_data.title || detail_data.product?.title || { ar: '', en: '' },
                    category: product_data.category || detail_data.product?.category || null,
                    category_id: product_data.category_id || detail_data.product?.category_id || null,
                    product_type_id: product_data.product_type_id || detail_data.product?.product_type_id || null,
                    pricing_unit_id: product_data.pricing_unit_id || product_data.pricingunitid || detail_data.product?.pricing_unit_id || null,
                    pricing_unit: pricingUnitCode,
                    // ✅ استخدام القيم الصحيحة
                    original_price: originalSellPrice,          // ✅ للتوافق مع الكود القديم
                    original_cost_price: originalCostPrice,     // ✅ من detail_data.cost_price
                    original_sell_price: originalSellPrice,     // ✅ من detail_data.original_sell_price
                    // ✅ إضافة pax_type_prices من بيانات API المنتج (دعم مسارين مختلفين للهيكل)
                    pax_type_prices: (detail_data.pax_type_prices || detail_data.product?.pax_type_prices || []).map(p => ({
                        pax_type_id: parseInt(p.pax_type_id),
                        pax_type_name: p.pax_type_name || null, // ✅ حفظ اسم نوع المسافر للعرض
                        price: parseFloat(p.price) || 0,
                        cost_price: parseFloat(p.cost_price) || 0,
                        // ✅ NEW: حفظ custom_cost_price من API للعرض والـ Hydration
                        custom_cost_price: p.custom_cost_price != null ? parseFloat(p.custom_cost_price) : null,
                    })),
                },

                // Selected supplier and service
                selected_supplier: detail_data.supplier || null,
                selected_service: detail_data.service || null,

                // Order
                order: product_data.order || 1,

                // Pricing data (from details)
                pricing: {
                    price: currentPrice,
                    cost_price: originalCostPrice,              // ✅ من detail_data.cost_price
                    custom_price: customPrice,
                    // ✅ FIX: Hydrate custom_cost_price for non-per-person products
                    custom_cost_price: detail_data.custom_cost_price != null
                        ? parseFloat(detail_data.custom_cost_price)
                        : null,
                    is_price_edited: isPriceEdited || (detail_data.custom_cost_price != null),
                    discount_type: detail_data.discount_type || 'zero',
                    discount_value: parseFloat(detail_data.discount) || 0,
                    // ✅ إضافة original_sell_price
                    original_sell_price: originalSellPrice,     // ✅ من detail_data.original_sell_price
                    // ✅ NEW: استخراج per-pax custom costs من API response للـ Hydration
                    custom_pax_prices: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                        if (ptp.custom_price != null) {
                            acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_price);
                        }
                        return acc;
                    }, {}),
                    custom_pax_costs: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                        if (ptp.custom_cost_price != null) {
                            acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_cost_price);
                        }
                        return acc;
                    }, {}),
                },

                // Dynamic fields (time, pickup_time, city, units, nights)
                dynamic_fields: buildDynamicFields(product_data),

                // Calculated values (will be recalculated by quotation logic)
                calculated: {
                    final_price_per_unit: parseFloat(detail_data.final_price) || currentPrice,
                    final_cost_per_unit: originalCostPrice,     // ✅ من detail_data.cost_price
                    quantity: 0,
                    total_price: 0,
                    total_cost: 0,
                },
            };
        }).filter(p => p !== null) || []; // Remove null products

        // Return day structure
        return {
            date: day.date,
            day_name: day.day_name || { ar: '', en: '' },
            products: products,
        };
    }) || [];

    // ============================================
    // 3. Generate empty days if no days exist
    // ============================================
    if (itinerary.length === 0 && programData.start_date && programData.durations) {
        const emptyDays = generateDayBoxes(
            programData.start_date,
            parseInt(programData.durations) || 0
        );
        itinerary.push(...emptyDays);
    }

    // ============================================
    // 4. Build and return form data
    // ============================================
    return {
        // Basic info
        title: programData.title || { ar: '', en: '' },
        description: programData.description || { ar: '', en: '' },
        agent_id: programData.agent_id || '',
        city_ids: programData.cities ? programData.cities.map(city => city.id) : [],
        start_date: programData.start_date || '',
        end_date: programData.end_date || '',
        durations_days: parseInt(programData.durations) || 0,
        durations_nights: parseInt(programData.nights) || 0,
        show_date_in_template: !!programData.show_date_in_template,

        // Cover image
        cover_image: programData.cover_image?.file_name || null,
        cover_image_preview: programData.cover_image?.url || programData.cover_image?.preview || '',

        // Pax types
        pax_types: paxTypes,

        // Itinerary
        itinerary: itinerary,

        // Global discount
        global_discount: {
            type: programData.discount_type || 'zero',
            value: parseFloat(programData.discount) || 0,
            target: programData.discount_target || 'none',
        },

        // Upload states
        uploadingCoverImage: false,
        coverImageError: '',
        coverImageSuccess: '',
    };
};
